import { format, parseISO } from 'date-fns';
import React from 'react';
import { Link } from 'react-router-dom';
import { MdOutlineFileDownload } from 'react-icons/md';
import { FundRequestFundInfo } from '~/types/fundrequests';

interface CampaignDetailsModalProps {
    open: boolean;
    onClose: () => void;
    fundInfo: FundRequestFundInfo | undefined;
    fundRequestId?: string | number;
}

const formatDate = (value?: string | null) => {
    if (!value) {
        return '—';
    }

    try {
        return format(parseISO(value), 'MM/dd/yy');
    } catch (error) {
        return value;
    }
};

const CampaignDetailsModal: React.FC<CampaignDetailsModalProps> = ({
    open,
    onClose,
    fundInfo,
    fundRequestId,
}) => {
    if (!open) return null;

    const isTransplant = fundInfo?.is_transplant ??
        (fundInfo?.patient?.campaign_type?.toLowerCase() === 'transplant');

    const transplantDate = isTransplant ? fundInfo?.transplant_date : null;
    const patientIsDeceased = fundInfo?.patient_is_deceased;
    const deathDate = patientIsDeceased ? fundInfo?.death_date : null;

    const deceasedLabel =
        typeof patientIsDeceased === 'boolean'
            ? patientIsDeceased
                ? 'Yes'
                : 'No'
            : 'Unknown';

    return (
        <div
            className='fixed inset-0 z-[99999] flex items-center justify-center bg-black bg-opacity-75'
            onClick={onClose}
        >
            <div
                className='relative w-full max-w-lg rounded-lg bg-white shadow-lg'
                onClick={(event) => event.stopPropagation()}
            >
                {/* Header with title and X button */}
                <div className='flex items-start justify-between p-8 pb-6'>
                    <h2 className='text-3xl font-normal'>
                        {fundInfo?.patient?.cf_patient_ntaf_id ? 
                            `${fundInfo.patient.cf_patient_ntaf_id} - Details` : 
                            'Details'
                        }
                    </h2>
                    <button
                        type='button'
                        className='text-2xl text-gray-400 hover:text-gray-600'
                        onClick={onClose}
                    >
                        ✕
                    </button>
                </div>

                {/* Content */}
                <div className='px-8 pb-6'>
                    <div className='space-y-6'>
                        <div>
                            <div className='text-xl text-gray-900'>
                                Open Date {formatDate(fundInfo?.funded_date)}
                            </div>
                        </div>
                        
                        <div>
                            <div className='text-xl text-gray-900'>
                                Disbursable Date {formatDate(fundInfo?.disbursable_date)}
                            </div>
                        </div>
                        
                        {isTransplant && (
                            <div>
                                <div className='text-xl text-gray-900'>
                                    Transplant Date: {formatDate(transplantDate)}
                                </div>
                            </div>
                        )}
                        
                        {patientIsDeceased && deathDate && (
                            <div>
                                <div className='text-xl text-gray-900'>
                                    Date of Death: {formatDate(deathDate)}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Action buttons */}
                <div className='flex items-center gap-3 px-8 pb-8'>
                    {fundRequestId && (
                        <Link
                            to={`/fund-requests/${fundRequestId}/pdf`}
                            target='_blank'
                            className='flex items-center gap-2 rounded-full border-2 border-primary px-6 py-2 text-sm font-medium uppercase text-primary transition-colors duration-200 hover:bg-primary hover:text-white'
                            title="Download PDF"
                        >
                            <MdOutlineFileDownload className='text-base' />
                            Download PDF
                        </Link>
                    )}
                    <button
                        type='button'
                        className='rounded-full border-2 border-gray-900 px-6 py-2 text-sm font-medium uppercase text-gray-900 hover:bg-gray-100'
                        onClick={onClose}
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CampaignDetailsModal;
